import { translateOrderStatus } from '@common/intl/order-status'
import type { OrderEventFragment } from '@gql'
import { OrderEventType } from '@gql'
import { Money } from '@ui/Money'
import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'

export function translateOrderEvent(
  intl: IntlShape,
  event: OrderEventFragment,
): string | ReactNode {
  switch (event.type) {
    case OrderEventType.DOCUMENT_CREATED: {
      const type = event.documentType ? event.documentType.name : null
      return intl.formatMessage(
        {
          defaultMessage: 'Document {type} was uploaded.',
          description: 'case history message',
        },
        {
          type: <b>{type}</b>,
        },
      )
    }

    case OrderEventType.DOCUMENT_EDITED: {
      const type = event.documentType ? event.documentType.name : null
      return intl.formatMessage(
        {
          defaultMessage: 'Document {type} was edited.',
          description: 'case history message',
        },
        {
          type: <b>{type}</b>,
        },
      )
    }

    case OrderEventType.DOCUMENT_DELETED: {
      const type = event.documentType ? event.documentType.name : null
      return intl.formatMessage(
        {
          defaultMessage: 'Document {type} was deleted.',
          description: 'case history message',
        },
        {
          type: <b>{type}</b>,
        },
      )
    }

    case OrderEventType.DOCUMENT_SIGNED: {
      const type = event.documentType ? event.documentType.name : null
      return intl.formatMessage(
        {
          defaultMessage: 'Document {type} was signed.',
          description: 'case history message',
        },
        {
          type: <b>{type}</b>,
        },
      )
    }

    case OrderEventType.CREATED:
      return intl.formatMessage({
        defaultMessage: 'Order was created.',
        description: 'case history message',
      })
    case OrderEventType.UPDATED:
      return intl.formatMessage({
        defaultMessage: 'Order was updated.',
        description: 'case history message',
      })
    case OrderEventType.PAYMENT_CREATED:
      return intl.formatMessage(
        {
          defaultMessage: 'Fee payment in amount of {money} was recorded.',
          description: 'case history message',
        },
        {
          money: (
            <Money
              money={{
                amount: event.parameters.paidAmount,
                currency: event.parameters.paidCurrency,
              }}
            />
          ),
        },
      )
    case OrderEventType.PAYMENT_UPDATED:
      return intl.formatMessage(
        {
          defaultMessage: 'Order payment was changed from {from} to {to}.',
          description: 'case history message',
        },
        {
          from: (
            <Money
              money={{
                amount: event.parameters.previousAmount,
                currency: event.parameters.paidCurrency,
              }}
            />
          ),
          to: (
            <Money
              money={{
                amount: event.parameters.newAmount,
                currency: event.parameters.paidCurrency,
              }}
            />
          ),
        },
      )
    case OrderEventType.PAYMENT_DELETED:
      return intl.formatMessage(
        {
          defaultMessage: 'Order payment for amount {money} was deleted.',
          description: 'case history message',
        },
        {
          money: (
            <Money
              money={{
                amount: event.parameters.previousAmount,
                currency: event.parameters.paidCurrency,
              }}
            />
          ),
        },
      )
    case OrderEventType.CANCELLED:
      return intl.formatMessage({
        defaultMessage: 'Order was cancelled.',
        description: 'order history message',
      })
    case OrderEventType.STATE_PROGRESSED:
      return intl.formatMessage(
        {
          defaultMessage: 'Order status changed to {status}.',
          description: 'order history message',
        },
        {
          status: <b>{translateOrderStatus(intl, event.parameters.newState)}</b>,
        },
      )
    case OrderEventType.CONFIRMED:
      return intl.formatMessage({
        defaultMessage: 'Order was confirmed.',
        description: 'order history message',
      })
    case OrderEventType.FULFILLED:
      return intl.formatMessage({
        defaultMessage: 'Order was fulfilled.',
        description: 'order history message',
      })
    case OrderEventType.LOCKED:
      return intl.formatMessage({
        defaultMessage: 'Order was locked.',
        description: 'order history message',
      })
    case OrderEventType.AMOUNT_ADJUSTED: {
      const { currency, current, previous } = event.parameters

      if (!currency || current == null || previous == null) {
        return intl.formatMessage({
          defaultMessage: 'Order amount was adjusted.',
          description: 'order history message',
        })
      }

      return intl.formatMessage(
        {
          defaultMessage: 'Order amount was adjusted from {from} to {to}.',
          description: 'order history message',
        },
        {
          from: <Money money={{ amount: previous, currency }} />,
          to: <Money money={{ amount: current, currency }} />,
        },
      )
    }
  }
}
